import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import popupImg from '../../images/popup.jpeg';

const HOME_PATHS = new Set(['/', '/index-2']);

function isHomePath(pathname) {
	return HOME_PATHS.has(pathname);
}

/**
 * Homepage popup (UAE banner). Shows whenever you land on home after a full page load (e.g. refresh).
 * Stays hidden for the rest of the SPA session after closing or after navigating away from home once.
 */
export default function FirstVisitHomePopup() {
	const location = useLocation();
	const prevPathRef = useRef(null);
	const [suppress, setSuppress] = useState(false);

	const handleContinue = useCallback(() => {
		setSuppress(true);
	}, []);

	useEffect(() => {
		const prev = prevPathRef.current;
		const curr = location.pathname;
		prevPathRef.current = curr;

		if (prev !== null && isHomePath(prev) && !isHomePath(curr)) {
			setSuppress(true);
		}
	}, [location.pathname]);

	const visible = isHomePath(location.pathname) && !suppress;

	useEffect(() => {
		if (!visible) return undefined;
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prevOverflow;
		};
	}, [visible]);

	useEffect(() => {
		if (!visible) return undefined;
		const onKey = (e) => {
			if (e.key === 'Escape') handleContinue();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [visible, handleContinue]);

	if (!visible) return null;

	return (
		<>
			<div className="sg-uae-popup-overlay" role="presentation">
				<div
					className="sg-uae-popup-shell"
					role="dialog"
					aria-modal="true"
					aria-labelledby="sg-uae-popup-title"
				>
					<h2 id="sg-uae-popup-title" className="sr-only">
						Welcome — Proud of the UAE
					</h2>
					<img src={popupImg} alt="Proud of the UAE — Star Giga" className="sg-uae-popup-img" />
					<button
						type="button"
						className="sg-uae-popup-continue"
						onClick={handleContinue}
						aria-label="Click here to continue"
					>
						<span className="sg-uae-popup-continue-shine" aria-hidden="true" />
						<span className="sg-uae-popup-continue-inner">
							{/* <i className="fa fa-check sg-uae-popup-continue-icon" aria-hidden="true" /> */}
							<span className="sg-uae-popup-continue-text">Click here to continue</span>
							<i className="fa fa-long-arrow-right sg-uae-popup-continue-arrow" aria-hidden="true" />
						</span>
					</button>
				</div>
			</div>
			<style>{`
				.sg-uae-popup-overlay {
					position: fixed;
					inset: 0;
					z-index: 10050;
					display: flex;
					align-items: center;
					justify-content: center;
					padding: clamp(16px, 4vw, 32px);
					background: rgba(8, 8, 10, 0.82);
					backdrop-filter: blur(12px);
					-webkit-backdrop-filter: blur(12px);
					animation: sg-uae-popup-fade 0.45s ease forwards;
				}
				@keyframes sg-uae-popup-fade {
					from { opacity: 0; }
					to { opacity: 1; }
				}
				.sg-uae-popup-shell {
					position: relative;
					width: min(94vw, 860px);
					max-height: min(90vh, 920px);
					border-radius: 16px;
					overflow: hidden;
					box-shadow:
						0 32px 90px rgba(0, 0, 0, 0.5),
						0 0 0 1px rgba(255, 255, 255, 0.12),
						0 0 60px rgba(227, 144, 1, 0.15);
					animation: sg-uae-popup-rise 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
				}
				@keyframes sg-uae-popup-rise {
					from {
						opacity: 0;
						transform: translateY(28px) scale(0.96);
					}
					to {
						opacity: 1;
						transform: translateY(0) scale(1);
					}
				}
				.sg-uae-popup-img {
					display: block;
					width: 100%;
					height: auto;
					vertical-align: middle;
				}
				.sg-uae-popup-continue {
					position: absolute;
					top: clamp(10px, 2.5vw, 18px);
					right: clamp(10px, 2.5vw, 18px);
					z-index: 3;
					border: none;
					cursor: pointer;
					padding: 0;
					background: transparent;
					font-family: inherit;
					animation: sg-uae-popup-cta-in 0.65s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both;
				}
				@keyframes sg-uae-popup-cta-in {
					from {
						opacity: 0;
						transform: translateY(-8px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				.sg-uae-popup-continue-inner {
					position: relative;
					z-index: 1;
					display: inline-flex;
					align-items: center;
					gap: 8px;
					padding: 10px 18px;
					border-radius: 6px;
					font-size: clamp(12px, 2.2vw, 14px);
					font-weight: 600;
					letter-spacing: 0.02em;
					color: #fff;
					background: #000;
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
					border: none;
					transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
				}
				.sg-uae-popup-continue-shine {
					display: none;
				}
				.sg-uae-popup-continue-icon {
					font-size: 1.05em;
					opacity: 0.95;
				}
				.sg-uae-popup-continue-arrow {
					font-size: 1.15em;
					transition: transform 0.25s ease;
				}
				.sg-uae-popup-continue:hover .sg-uae-popup-continue-inner,
				.sg-uae-popup-continue:focus-visible .sg-uae-popup-continue-inner {
					transform: translateY(-1px);
					background: #1a1a1a;
					box-shadow: 0 6px 16px rgba(0, 0, 0, 0.45);
				}
				.sg-uae-popup-continue:hover .sg-uae-popup-continue-arrow,
				.sg-uae-popup-continue:focus-visible .sg-uae-popup-continue-arrow {
					transform: translateX(4px);
				}
				.sg-uae-popup-continue:active .sg-uae-popup-continue-inner {
					transform: translateY(0);
				}
				.sg-uae-popup-continue:focus-visible {
					outline: none;
				}
				.sg-uae-popup-continue:focus-visible .sg-uae-popup-continue-inner {
					outline: 2px solid #fff;
					outline-offset: 3px;
				}
				@media (max-width: 767.98px) {
					.sg-uae-popup-overlay {
						padding: 12px;
						align-items: center;
					}
					.sg-uae-popup-shell {
						width: 100%;
						max-width: 100%;
						border-radius: 10px;
						max-height: none;
					}
					.sg-uae-popup-img {
						width: 100%;
						height: auto;
						object-fit: contain;
					}
					.sg-uae-popup-continue {
						        top: 3px;
        right: 6px;
        left: auto;
					}
					.sg-uae-popup-continue-inner {
						padding: 4px 7px;
						gap: 6px;
						font-size: 11px;
						border-radius: 6px;
						white-space: nowrap;
					}
					.sg-uae-popup-continue-icon {
						font-size: 12px;
					}
					.sg-uae-popup-continue-arrow {
						font-size: 12px;
					}
				}
				.sg-uae-popup-overlay .sr-only {
					position: absolute;
					width: 1px;
					height: 1px;
					padding: 0;
					margin: -1px;
					overflow: hidden;
					clip: rect(0, 0, 0, 0);
					white-space: nowrap;
					border: 0;
				}
			`}</style>
		</>
	);
}
